import React, { useEffect, useState, useCallback, useContext } from 'react';
import { getDepts, createDept, updateDept, refreshDeptHierarchy  } from '../../api/deptApi';
import Papa from 'papaparse';
import DeptView from './DeptView';
import DeptEdit from './DeptEdit';
import DeptForm from './DeptForm';   // ✅ 등록 컴포넌트 이름 변경
import { getManagers } from '../../api/managerApi';
import { AuthContext } from '../../context/AuthContext';
import '../../css/dept.css';

function DeptList() {
  const [deptTree, setDeptTree] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [mode, setMode] = useState('view'); // view | edit | new
  const [csvFile, setCsvFile] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedComCode, setSelectedComCode] = useState('');
  const { token } = useContext(AuthContext);

  const selectedComName = companies.find((c) => c.M_ComCode === selectedComCode)?.M_ComName || '';

  const buildDeptTree = useCallback((depts) => {
    const map = {};
    const tree = [];
    depts.forEach((dept) => {
      map[dept.dept_code] = { ...dept, children: [] };
    });
    depts.forEach((dept) => {
      if (dept.parent_dept_code && map[dept.parent_dept_code]) {
        map[dept.parent_dept_code].children.push(map[dept.dept_code]);
      } else {
        tree.push(map[dept.dept_code]);
      }
    });
    return tree;
  }, []);

  const loadTree = useCallback(() => {
    if (!selectedComCode) {
      setDeptTree([]);
      return;
    }
    getDepts(selectedComCode, token).then((res) => {
      const tree = buildDeptTree(res.data);
      setDeptTree(tree);
    });
  }, [selectedComCode, token, buildDeptTree]);

  // 전체 회사 목록 로드
  useEffect(() => {
    getManagers(token)
      .then((data) => setCompanies(data))
      .catch((err) => console.error('회사 목록 조회 실패:', err));
  }, [token]);

  const handleComCodeChange = (e) => {
    const comCode = e.target.value;
    setSelectedComCode(comCode);
    setSelectedDept(null);
    setMode('view');
  };

  useEffect(() => {
    loadTree();
  }, [loadTree]);

  const handleCsvProcess = () => {
    if (!selectedComCode) {
      alert('회사를 먼저 선택하세요.');
      return;
    }
    if (!csvFile) {
      alert('CSV 파일을 먼저 선택하세요.');
      return;
    }

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      encoding: "UTF-8",
      complete: async (result) => {
        const allDepts = await getDepts(selectedComCode, token);
        for (const row of result.data) {
          try {
            const exists = allDepts.data.find((d) => d.dept_code === row.dept_code);
            if (exists) {
              await updateDept(row.dept_code, row, selectedComCode, token);
              console.log(`✅ Updated: ${row.dept_code}`);
            } else {
              await createDept(row, selectedComCode, token);
              console.log(`➕ Created: ${row.dept_code}`);
            }
          } catch (err) {
            console.error('CSV 처리 오류:', err);
          }
        }
        await refreshDeptHierarchy(selectedComCode, token);
        loadTree();
        alert('CSV 업로드 완료!');
      },
    });
  };

  const handleRefreshHierarchy = async () => {
    try {
      if (!selectedComCode) {
        alert('회사를 먼저 선택하세요.');
        return;
      }
      await refreshDeptHierarchy(selectedComCode, token);
      loadTree();
      alert('부서 상·하위 정보 재정리 완료!');
    } catch (err) {
      console.error('부서 재정리 오류:', err);
      alert('부서 재정리 실패');
    }
  };

  const renderTree = (nodes) => (
    <ul className="dept-tree">
      {nodes.map(node => (
        <li key={node.dept_code}>
          <span
            className="dept-node"
            onClick={() => {
              setSelectedDept(node.dept_code);
              setMode('view');
            }}
          >
            <i className="fas fa-building me-2 text-secondary"></i>
            {node.dept_name} <small className="text-muted">({node.dept_code})</small>
          </span>
          {node.children.length > 0 && renderTree(node.children)}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="row mt-4">
      {/* 좌측: 부서 트리 */}
      <div className="col-6 border-end">
        <h3>📂 부서 트리</h3>
        <select
            className="form-select"
            style={{ minWidth: '180px', maxWidth: '240px' }}
            value={selectedComCode}
            onChange={handleComCodeChange}
          >
            <option value="">회사를 선택해주세요.</option>
            {companies.map((c) => (
              <option key={c.M_ComCode} value={c.M_ComCode}>{c.M_ComName}</option>
            ))}
          </select>
        <div className="d-flex mb-3 gap-2">
          

          {/* ✅ 부서 등록 버튼 */}
          <button
            className="btn btn-success"
            disabled={!selectedComCode}
            onClick={() => {
              setSelectedDept(null);
              setMode('new');
            }}
          >
            ➕ 부서 등록
          </button>
          {/* ✅ CSV 업로드 input + 버튼 */}
          <input
            type="file"
            accept=".csv"
            className="form-control"
            style={{ maxWidth: '200px' }}
            disabled={!selectedComCode}
            onChange={(e) => setCsvFile(e.target.files[0])}
          />
          <button
            className="btn btn-primary"
            disabled={!selectedComCode}
            onClick={handleCsvProcess}
          >
            📤 업로드
          </button>
          <a
            href="/sampleFile/deptSample.csv"
            download="deptSample.csv"
            className="btn btn-info"
          >
            📥 샘플 다운로드
          </a>
          <button
            className="btn btn-warning"
            disabled={!selectedComCode}
            onClick={handleRefreshHierarchy}
          >
            🔄 부서 재정리
          </button>

        </div>
        {!selectedComCode ? <p>회사를 선택해주세요.</p> : renderTree(deptTree)}
      </div>

      {/* 우측: 상세 / 편집 / 등록 */}
      <div className="col-6">
        {mode === 'view' && selectedDept && (
          <DeptView 
            code={selectedDept} 
            comCode={selectedComCode}
            comName={selectedComName}
            onEdit={() => setMode('edit')} 
            onDeleted={() => {
              loadTree();
              setSelectedDept(null);
            }}
          />
        )}
        {mode === 'edit' && selectedDept && (
          <DeptEdit
            code={selectedDept}
            comCode={selectedComCode}
            comName={selectedComName}
            onCancel={() => setMode('view')}
            onUpdated={() => {
              loadTree();
              setMode('view');
            }}
          />
        )}
        {mode === 'new' && (
          <DeptForm
            comCode={selectedComCode}
            comName={selectedComName}
            onCancel={() => setMode('view')}
            onCreated={() => {
              loadTree();
              setMode('view');
            }}
          />
        )}
        {!selectedDept && mode === 'view' && (
          <p>{selectedComName ? `${selectedComName} 부서를 선택하세요` : '회사를 선택하세요'}</p>
        )}
      </div>
    </div>
  );
}

export default DeptList;